import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

const DOT = { ok: "bg-emerald-500", warn: "bg-amber-500", down: "bg-red-600", unknown: "bg-slate-300" };
const LABEL = { ok: "OK", warn: "UWAGA", down: "AWARIA", unknown: "brak danych" };

function StatusDot({ s }) {
  return (
    <span className="inline-flex items-center gap-2" data-testid={`status-${s}`}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[s] || DOT.unknown}`} />
      <span className="text-xs font-semibold uppercase text-slate-600">{LABEL[s] || s}</span>
    </span>
  );
}

function Card({ title, status, children, testid }) {
  return (
    <section className="rounded-xl border border-slate-300 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h2>
        {status && <StatusDot s={status} />}
      </div>
      <div className="mt-3 text-sm">{children}</div>
    </section>
  );
}

function Row({ k, v, bad }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
      <span className="text-xs text-slate-500">{k}</span>
      <span className={`break-all text-right text-xs font-medium ${bad ? "text-red-700" : "text-slate-800"}`}>{v}</span>
    </div>
  );
}

export default function AdminHealthPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/admin/platform-health");
      setData(r.data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  if (!user || user.role !== "admin") return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="health-denied">
      <p className="text-sm text-slate-600">Dostęp wyłącznie dla administratora (MFA).</p>
    </div>);

  const rt = data?.pdf_runtime;
  const src = data?.sources;
  const auth = data?.auth;
  const jobs = data?.jobs;
  const exp = data?.exports;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="admin-health-page">
      <Helmet><title>Kondycja platformy — PlanGmina</title></Helmet>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kondycja platformy</h1>
          {data && <p className="mt-1 text-xs text-slate-500">Snapshot: {data.generated_at?.slice(0, 19).replace("T", " ")} UTC · odczyt bieżącego stanu, bez efektów ubocznych</p>}
        </div>
        <div className="flex items-center gap-3">
          {data && <StatusDot s={data.overall} />}
          <button onClick={load} disabled={loading} data-testid="health-refresh"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
            {loading ? "Odświeżanie…" : "Odśwież"}
          </button>
        </div>
      </div>
      {err && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="health-error">{err}</p>}
      {!data && !err && <p className="mt-10 text-center text-sm text-slate-500" data-testid="health-loading">Ładowanie…</p>}

      {data && (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Card title="Runtime PDF (WeasyPrint)" status={rt.status} testid="health-pdf-runtime">
            <Row k="WeasyPrint" v={rt.weasyprint || "BRAK"} bad={!rt.weasyprint} />
            <Row k="Biblioteki natywne (find_library)" v={`${Object.values(rt.find_library).filter(Boolean).length}/8`} bad={!Object.values(rt.find_library).every(Boolean)} />
            <Row k="Ładowanie CDLL (soname)" v={`${Object.values(rt.cdll).filter(Boolean).length}/8`} bad={!Object.values(rt.cdll).every(Boolean)} />
            <Row k="Fonty" v={`${rt.fonts.count} (DejaVu: ${rt.fonts.dejavu ? "tak" : "NIE"})`} bad={!rt.fonts.dejavu} />
            <Row k="Testowy render (diakrytyki)" v={rt.weasyprint_render.ok ? `OK (${rt.weasyprint_render.bytes} B)` : (rt.weasyprint_render.error || "BŁĄD")} bad={!rt.weasyprint_render.ok} />
            <Row k="Kod backendu załadowany" v={rt.backend_code_loaded_at?.slice(0, 19).replace("T", " ")} />
          </Card>

          <Card title="Uwierzytelnianie (24 h)" status={auth.status} testid="health-auth">
            <Row k="Logowania OK" v={auth.login_success} />
            <Row k="Logowania nieudane" v={auth.login_failed} bad={auth.login_failed > 50} />
            <Row k="Wyzwania MFA" v={auth.mfa_challenges} />
            <Row k="MFA nieudane" v={auth.mfa_failed} bad={auth.mfa_failed > 10} />
          </Card>

          <Card title="Źródła / adaptery" status={Object.values(src.adapters).some((a) => a.health === "down") ? "warn" : "ok"} testid="health-sources">
            <div className="max-h-72 overflow-y-auto">
              {Object.entries(src.adapters).map(([name, a]) => (
                <div key={name} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1.5" data-testid={`health-source-${name}`}>
                  <span className="text-xs text-slate-600">{name}</span>
                  <span className="flex items-center gap-2 text-right">
                    <span className="text-[11px] text-slate-400">{a.last_at ? a.last_at.slice(0, 16).replace("T", " ") : "—"}</span>
                    <StatusDot s={a.health || "unknown"} />
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2 border-t border-slate-200 pt-2">
              <Row k="Snapshot GDOŚ" v={src.gdos_snapshot.loaded ? `${src.gdos_snapshot.features} obiektów · ${src.gdos_snapshot.loaded_at?.slice(0, 10)}` : "BRAK"} bad={!src.gdos_snapshot.loaded} />
              <Row k="Rastry terenu (cache)" v={src.terrain_rasters_cached} />
            </div>
          </Card>

          <Card title="Zadania w tle" status={jobs.status} testid="health-jobs">
            <Row k="Ostatni cron" v={jobs.last_cron_age_hours != null ? `${jobs.last_cron_age_hours} h temu` : "brak"} bad={jobs.last_cron_age_hours > 26} />
            {jobs.backup_recent[0] && <Row k="Ostatni backup" v={`${jobs.backup_recent[0].status} · ${(jobs.backup_recent[0].at || "").slice(0, 16).replace("T", " ")}`} bad={jobs.backup_recent[0].status !== "success" && jobs.backup_recent[0].status !== "ok"} />}
            {jobs.digest_recent[0] && <Row k="Ostatni digest" v={`${jobs.digest_recent[0].status} · ${jobs.digest_recent[0].recipient || ""} · ${(jobs.digest_recent[0].sent_at || jobs.digest_recent[0].created_at || "").slice(0, 16).replace("T", " ")}`} />}
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Ostatnie cron_runs</p>
              {jobs.cron_recent.slice(0, 5).map((c, i) => (
                <Row key={i} k={c.job || "job"} v={(c.received_at || "").slice(0, 19).replace("T", " ")} />
              ))}
            </div>
          </Card>

          <Card title="Eksporty" status={exp.status} testid="health-exports">
            <Row k="Ostatni eksport profesjonalny" v={exp.last_professional ? `${exp.last_professional.generated_at?.slice(0, 16).replace("T", " ")} · ${exp.last_professional.parcel_id} · sha ${exp.last_professional.pdf_sha256_prefix}…` : "brak"} />
            <Row k="Eksporty prof. (24 h / ogółem)" v={`${exp.professional_24h} / ${exp.professional_total}`} />
            <Row k="Ostatni eksport akademicki" v={exp.last_academic ? `${exp.last_academic.generated_at?.slice(0, 16).replace("T", " ")} · ${exp.last_academic.institution_id}` : "brak"} />
            <Row k="Eksporty akad. ogółem" v={exp.academic_total} />
          </Card>

          <Card title="Skala / liczniki" status="ok" testid="health-totals">
            <Row k="Użytkownicy" v={data.totals.users} />
            <Row k="Organizacje" v={data.totals.organizations} />
            <Row k="Raporty" v={data.totals.reports} />
            <Row k="Portfele" v={data.totals.portfolios} />
            <Row k="Uczelnie EDU / członkowie" v={`${data.totals.edu_institutions} / ${data.totals.edu_members}`} />
            <Row k="Zdarzenia audytu" v={data.totals.audit_events} />
          </Card>
        </div>
      )}
    </div>
  );
}
